import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  FileText, RefreshCw, ShoppingCart, Smartphone, Apple,
  Headphones, Settings, Link as LinkIcon, ArrowUpRight, CheckCircle2
} from "lucide-react";
import PageHero from "../components/PageHero";
import { SERVICES, servicePath, serviceSlug } from "../lib/constants";

const ICON_MAP = {
  FileText, RefreshCw, ShoppingCart, Smartphone, Apple,
  Headphones, Settings, Link: LinkIcon,
};

const DELIVERABLES = [
  "Discovery doc & 90-day roadmap",
  "Architecture diagram & tech stack",
  "Sprint-by-sprint demos every 2 weeks",
  "Full source code + documentation",
  "Production handover & runbooks",
  "30 / 60 / 90-day post-launch support",
];

export default function ServicesPage() {
  return (
    <>
      <PageHero
        eyebrow="Services"
        breadcrumb={[{ label: "Services" }]}
        title={<>Web, mobile & CRM, <span className="text-gradient-cyan italic">built for desks</span> that scale.</>}
        lede="Eight focused engagements covering everything from a marketing site rebuild to a full CRM rollout. Pick one, stack a few, or let us recommend a package."
      />

      {/* Services list with detail rows */}
      <section className="py-16 sm:py-24">
        <div className="max-w-7xl mx-auto px-6">
          <div className="space-y-4">
            {SERVICES.map((s, i) => {
              const Icon = ICON_MAP[s.icon] || FileText;
              return (
                <motion.div
                  key={s.title}
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.05 }}
                  data-testid={`services-row-${i}`}
                  className="group"
                >
                <Link
                  to={servicePath(serviceSlug(s.title))}
                  className="grid grid-cols-12 gap-4 items-center p-6 rounded-2xl border border-white/10 hover:border-[#00C2FF]/40 hover:bg-[#00C2FF]/[0.03] transition-all"
                >
                  <div className="col-span-2 sm:col-span-1 font-mono text-xs text-gray-500 tabular-nums tracking-widest">
                    {String(i + 1).padStart(2, "0")}
                  </div>
                  <div className="col-span-2 sm:col-span-1">
                    <div className="w-12 h-12 rounded-xl bg-[#00C2FF]/10 border border-[#00C2FF]/30 flex items-center justify-center text-[#00C2FF] group-hover:scale-105 transition-transform">
                      <Icon className="w-5 h-5" />
                    </div>
                  </div>
                  <div className="col-span-8 sm:col-span-4">
                    <h3 className="font-display text-lg sm:text-xl font-semibold">{s.title}</h3>
                  </div>
                  <div className="hidden sm:block col-span-5 text-sm text-gray-400 leading-relaxed">
                    {s.desc}
                  </div>
                  <span className="hidden sm:flex col-span-1 justify-end">
                    <ArrowUpRight className="w-5 h-5 text-gray-500 group-hover:text-[#00C2FF] group-hover:translate-x-1 group-hover:-translate-y-1 transition-all" />
                  </span>
                </Link>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* What you get */}
      <section className="py-20 border-t border-white/10">
        <div className="max-w-5xl mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-12 items-start">
            <div>
              <div className="flex items-center gap-3 mb-5">
                <span className="w-10 h-px bg-[#00C2FF]" />
                <span className="font-mono text-[11px] uppercase tracking-[0.4em] text-[#00C2FF] font-bold">
                  / What you get
                </span>
              </div>
              <h2 className="font-display text-3xl sm:text-4xl font-extrabold leading-tight tracking-tighter">
                Six concrete deliverables, every engagement.
              </h2>
              <p className="text-gray-400 mt-4 leading-relaxed">
                Whether you book the smallest static-site project or a full CRM rollout, the
                engagement structure is identical — and you keep ownership of everything we build.
              </p>
            </div>
            <ul className="space-y-3">
              {DELIVERABLES.map((d, i) => (
                <motion.li
                  key={d}
                  initial={{ opacity: 0, x: 12 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.06 }}
                  className="flex items-start gap-3 p-4 rounded-xl theme-panel border"
                >
                  <CheckCircle2 className="w-5 h-5 text-[#00C2FF] shrink-0 mt-0.5" />
                  <span className="text-sm text-gray-200">{d}</span>
                </motion.li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 border-t border-white/10">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <h2 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight">
            Ready to scope a project?
          </h2>
          <p className="text-gray-400 mt-3">
            Tell us what you're trying to ship. We'll come back with a plan and a price within 48h.
          </p>
          <Link
            to="/contact"
            className="btn-cyan inline-flex items-center gap-2 mt-8 px-8 py-3.5 rounded-full font-semibold"
          >
            Get a quote <ArrowUpRight className="w-4 h-4" />
          </Link>
        </div>
      </section>
    </>
  );
}
